import type {
  FormEmbedField,
  ResolvedCallRailAttribution,
} from "./types.js";

const DEFAULT_CAPTURE_TIMEOUT_MS = 750;
const READINESS_POLL_INTERVAL_MS = 25;
const FORMVUE_CAPTURE_FIELD = "formvue_capture_id";
const capturedIds = new Set<string>();

interface CallRailTracker {
  captureForm(form: HTMLFormElement, callback: () => void): void;
}

type CallRailWindow = Window & {
  CallTrk?: CallRailTracker;
};

export interface CallRailContact {
  name?: string;
  phone?: string;
  email?: string;
}

export interface CallRailPendingMetadata {
  schemaVersion: 1;
  captureId: string;
  status: "pending";
}

export interface PreparedCallRailCapture {
  captureId: string;
  contact: CallRailContact;
  metadata: CallRailPendingMetadata;
}

export type CallRailCaptureResult =
  | "captured"
  | "duplicate"
  | "unavailable"
  | "timeout"
  | "error";

interface CaptureCallRailOptions {
  document?: Document;
  timeoutMs?: number;
}

function cleanContactValue(value: unknown): string | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const cleaned = String(value).trim();
  return cleaned || undefined;
}

function normalizeFieldIdentity(field: FormEmbedField): string {
  return `${field.name} ${field.label}`
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
}

function isGenericPersonNameField(field: FormEmbedField): boolean {
  const identities = [field.name, field.label].map((value) =>
    value
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim()
      .toLowerCase(),
  );

  return identities.some((identity) =>
    /^(?:(?:your|customer|contact|client|homeowner|applicant|person)\s+)?(?:full\s+)?name$/.test(
      identity,
    ),
  );
}

/**
 * Pulls only the contact values CallRail documents for external form capture.
 * Arbitrary FormVue answers are intentionally not copied into the temporary
 * light-DOM form.
 */
export function collectCallRailContact(
  preData: Record<string, unknown>,
  fields: FormEmbedField[],
  formattedPhone?: string | null,
): CallRailContact {
  let fullName: string | undefined;
  let firstName: string | undefined;
  let lastName: string | undefined;
  let email: string | undefined;

  for (const field of fields) {
    const value = cleanContactValue(preData[field.name]);
    if (!value) continue;

    if (!email && field.type === "email") {
      email = value;
      continue;
    }

    if (field.type !== "text") continue;

    const identity = normalizeFieldIdentity(field);
    if (/\b(company|business|organization)\b/.test(identity)) continue;

    if (!firstName && /\b(first|given)\s*name\b/.test(identity)) {
      firstName = value;
    } else if (!lastName && /\b(last|family|sur)\s*name\b/.test(identity)) {
      lastName = value;
    } else if (!fullName && isGenericPersonNameField(field)) {
      fullName = value;
    }
  }

  const combinedName = [firstName, lastName].filter(Boolean).join(" ");

  return {
    name: combinedName || fullName,
    phone: cleanContactValue(formattedPhone),
    email,
  };
}

function generateCaptureId(): string | null {
  try {
    const cryptoApi = globalThis.crypto;
    if (!cryptoApi) return null;

    if (typeof cryptoApi.randomUUID === "function") {
      return cryptoApi.randomUUID();
    }

    if (typeof cryptoApi.getRandomValues !== "function") return null;

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10, 16).join(""),
    ].join("-");
  } catch {
    return null;
  }
}

/**
 * Generates the correlation id immediately before a FormLink request. A
 * capture is prepared only when the integration is enabled and the host-side
 * form supplied a real contact value CallRail can recognize.
 */
export function prepareCallRailCapture(
  config: ResolvedCallRailAttribution | undefined,
  contact: CallRailContact,
): PreparedCallRailCapture | null {
  const cleanedContact = {
    name: cleanContactValue(contact.name),
    phone: cleanContactValue(contact.phone),
    email: cleanContactValue(contact.email),
  };

  if (
    !config?.enabled ||
    (!cleanedContact.name && !cleanedContact.phone && !cleanedContact.email)
  ) {
    return null;
  }

  const captureId = generateCaptureId();
  if (!captureId) return null;

  return {
    captureId,
    contact: cleanedContact,
    metadata: {
      schemaVersion: 1,
      captureId,
      status: "pending",
    },
  };
}

function appendInput(
  form: HTMLFormElement,
  type: "text" | "tel" | "email" | "hidden",
  name: string,
  value: string | undefined,
): void {
  if (!value) return;

  const input = form.ownerDocument.createElement("input");
  input.type = type;
  input.name = name;
  input.value = value;
  form.appendChild(input);
}

function buildCaptureForm(
  doc: Document,
  capture: PreparedCallRailCapture,
): HTMLFormElement {
  const form = doc.createElement("form");
  form.method = "post";
  form.action = doc.defaultView?.location.href ?? "";
  form.style.display = "none";
  form.setAttribute("aria-hidden", "true");
  form.setAttribute("data-formvue-callrail-capture", "");

  appendInput(form, "text", "name", capture.contact.name);
  appendInput(form, "tel", "phone", capture.contact.phone);
  appendInput(form, "email", "email", capture.contact.email);
  appendInput(form, "hidden", FORMVUE_CAPTURE_FIELD, capture.captureId);

  return form;
}

/**
 * Sends the already-created FormLink's contact data to CallRail using its
 * documented manual form API. Every exit is resolved, never rejected, so
 * CallRail cannot fail or delay the FormVue flow beyond the bounded timeout.
 */
export async function captureCallRail(
  capture: PreparedCallRailCapture,
  options: CaptureCallRailOptions = {},
): Promise<CallRailCaptureResult> {
  try {
    const doc =
      options.document ??
      (typeof document === "undefined" ? undefined : document);
    if (!doc?.body) {
      return "unavailable";
    }

    return await new Promise<CallRailCaptureResult>((resolve) => {
      let settled = false;
      let captureStarted = false;
      let form: HTMLFormElement | null = null;
      let readinessTimer: ReturnType<typeof setTimeout> | undefined;

      const finish = (result: CallRailCaptureResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(deadlineTimer);
        if (readinessTimer) clearTimeout(readinessTimer);
        try {
          form?.remove();
        } catch {
          // Cleanup must not affect the FormVue submission.
        }
        resolve(result);
      };

      const deadlineTimer = setTimeout(
        () => finish(captureStarted ? "timeout" : "unavailable"),
        Math.max(0, options.timeoutMs ?? DEFAULT_CAPTURE_TIMEOUT_MS),
      );

      const attemptCapture = () => {
        if (settled) return;

        let tracker: CallRailTracker | undefined;
        try {
          tracker = (doc.defaultView as CallRailWindow | null)?.CallTrk;
        } catch {
          finish("error");
          return;
        }

        if (typeof tracker?.captureForm !== "function") {
          readinessTimer = setTimeout(
            attemptCapture,
            READINESS_POLL_INTERVAL_MS,
          );
          return;
        }

        if (capturedIds.has(capture.captureId)) {
          finish("duplicate");
          return;
        }
        capturedIds.add(capture.captureId);
        captureStarted = true;

        try {
          form = buildCaptureForm(doc, capture);
          doc.body.appendChild(form);
          tracker.captureForm(form, () => finish("captured"));
        } catch {
          finish("error");
        }
      };

      attemptCapture();
    });
  } catch {
    return "error";
  }
}
